import { Skeleton as MantineSkeleton, parseThemeColor, darken } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import styles from "./skeleton.module.scss";
import type { CustomSkeletonFactory, IProps } from "./skeleton.types";
import { useTheme } from "../../hooks";
import { useMemo } from "react";

/**
 * Skeleton component for displaying loading placeholders.
 * @param {string} props.color - The color of the skeleton.
 * @param {object} props.style - Custom styles for the skeleton.
 * @param {number} props.bgDarkenAlpha - Alpha value for darkening the background color.
 * @param {number} props.animatedDarkenAlpha - Alpha value for darkening the animated color.
 * @param {boolean} props.disableDarken - Whether to disable color darkening.
 * @returns {JSX.Element} The Skeleton component.
 */
const Skeleton = componentWrapper<CustomSkeletonFactory, IProps>(
    "Skeleton",
    ({ color, style, bgDarkenAlpha, animatedDarkenAlpha, disableDarken, ...otherProps }, ref) => {
        const theme = useTheme();
        const colors = color ? theme.variantColorResolver({ color: color, theme, variant: "light" }) : undefined;
        const parsedThemeColor = color ? parseThemeColor({ color: color, theme }) : null;

        const computedColor = useMemo(() => {
            return parsedThemeColor?.isThemeColor && parsedThemeColor?.shade === undefined
                ? `var(--mantine-color-${parsedThemeColor.color}-6)`
                : colors?.color;
        }, [parsedThemeColor, colors]);

        const bgColor = useMemo(() => {
            if (computedColor) {
                if (disableDarken) return computedColor;
                return darken(computedColor, bgDarkenAlpha ?? 0.05);
            }
            return undefined;
        }, [computedColor, disableDarken, bgDarkenAlpha]);

        const animatedColor = useMemo(() => {
            if (computedColor) {
                if (disableDarken) return computedColor;
                return darken(computedColor, animatedDarkenAlpha ?? 0.15);
            }
            return undefined;
        }, [computedColor, disableDarken, animatedDarkenAlpha]);

        return (
            <MantineSkeleton
                ref={ref}
                classNames={{ root: styles["root"] }}
                style={[
                    {
                        "--skeleton-bg-color": bgColor,
                        "--skeleton-animated-color": animatedColor,
                    },
                    style,
                ]}
                {...otherProps}
            />
        );
    }
);

export { Skeleton };
